import { Calendar, CheckCheck, ArrowUpRight } from 'lucide-react';

import type { InterviewsListProps } from '@shared/widget-schemas';
import { cn } from '@/lib/utils';
import { ChatCard, mix } from './chat-widget-ui';
import { capitalizeFirst } from './textFormat';
import { SkBar, SkChip, SkCircle } from './skeletons';

const BLUE = 'var(--color-blue-400)';
const PURPLE = 'var(--color-purple-400)';
const AVATAR_BG = 'var(--av-neutral, var(--color-gray-500))';

function initials(name: string): string {
  return name
    .split(' ')
    .map((w) => w[0])
    .join('')
    .slice(0, 2);
}

function Avatar({ name }: { name: string }) {
  return (
    <span
      className="inline-flex size-8 shrink-0 items-center justify-center rounded-full text-[11px] font-bold text-white"
      style={{ background: AVATAR_BG }}
      aria-hidden
    >
      {initials(name)}
    </span>
  );
}

export function InterviewsListCardWidget({ mode, items }: InterviewsListProps) {
  const completed = mode === 'completed';
  const HeadIcon = completed ? CheckCheck : Calendar;
  const title = completed ? 'Completed interviews' : 'Planned interviews';

  return (
    <ChatCard className="flex flex-col gap-3 p-[18px] text-sm">
      {/* header */}
      <div className="flex flex-wrap items-center gap-[11px]">
        <span
          className="inline-flex size-7 shrink-0 items-center justify-center rounded-lg"
          style={{ background: mix(BLUE, 14), border: `1px solid ${mix(BLUE, 28)}`, color: BLUE }}
        >
          <HeadIcon className="size-[14px]" aria-hidden />
        </span>
        <span className="text-[15px] font-bold text-foreground">{title}</span>
        <span
          className="font-mono text-[10.5px] uppercase tracking-[0.05em]"
          style={{ color: 'var(--foreground-subtle)' }}
        >
          {items.length}
        </span>
      </div>

      <div className={cn('flex flex-col', completed ? 'gap-2' : 'gap-0')}>
        {items.map((item, i) => {
          if (completed) {
            return (
              <div
                key={item.id}
                className="grid grid-cols-[34px_1fr_auto] items-center gap-[11px] rounded-xl border border-border bg-secondary px-3 py-[11px]"
              >
                <Avatar name={item.name} />
                <div className="flex min-w-0 flex-col gap-0.5">
                  <div className="flex items-baseline gap-2">
                    <span className="text-[12.5px] font-bold text-foreground">{item.name}</span>
                    {item.role && (
                      <span className="truncate text-[10.5px] text-muted-foreground">{capitalizeFirst(item.role)}</span>
                    )}
                  </div>
                  {item.summary && (
                    <span className="text-[11.5px] leading-[1.45] text-muted-foreground text-pretty">
                      {capitalizeFirst(item.summary)}
                    </span>
                  )}
                  {item.signalsCount != null && (
                    <span className="mt-0.5 font-mono text-[10px] font-semibold" style={{ color: PURPLE }}>
                      <strong className="font-bold">{item.signalsCount}</strong> signals extracted
                    </span>
                  )}
                </div>
                <span
                  className="inline-flex size-[26px] shrink-0 items-center justify-center rounded-md"
                  style={{ color: 'var(--foreground-subtle)' }}
                  aria-hidden
                >
                  <ArrowUpRight className="size-[14px]" />
                </span>
              </div>
            );
          }

          const tone = item.tagColor || PURPLE;
          return (
            <div
              key={item.id}
              className="grid grid-cols-[34px_1fr_auto] items-center gap-[11px] py-[11px]"
              style={i > 0 ? { borderTop: '1px solid color-mix(in srgb, var(--border) 60%, transparent)' } : undefined}
            >
              <Avatar name={item.name} />
              <div className="flex min-w-0 flex-col">
                <span className="text-[12.5px] font-semibold leading-[1.3] text-foreground">{item.name}</span>
                {item.org && (
                  <span className="truncate text-[11px] leading-[1.35] text-muted-foreground">{item.org}</span>
                )}
                {item.scheduledAt && (
                  <span className="mt-0.5 font-mono text-[10.5px]" style={{ color: 'var(--foreground-subtle)' }}>
                    {item.scheduledAt}
                  </span>
                )}
              </div>
              {item.tag && (
                <span
                  className="whitespace-nowrap rounded-full px-2 py-[3px] font-mono text-[10px] font-semibold"
                  style={{ color: tone, background: mix(tone, 14), border: `1px solid ${mix(tone, 30)}` }}
                >
                  {item.tag}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </ChatCard>
  );
}

export function InterviewsListCardSkeleton({ className, rows = 3 }: { className?: string; rows?: number }) {
  return (
    <ChatCard className={cn('flex flex-col gap-3 p-[18px]', className)}>
      <div className="flex items-center gap-2.5">
        <SkCircle size="28px" radius="8px" />
        <SkBar width="130px" height="13px" />
        <span className="ml-auto">
          <SkChip width="40px" />
        </span>
      </div>
      {Array.from({ length: rows }).map((_, i) => (
        <div
          key={i}
          className="grid grid-cols-[34px_1fr_auto] items-center gap-[11px] rounded-xl border border-border bg-secondary px-3 py-[11px]"
        >
          <SkCircle size="34px" />
          <div className="flex flex-col gap-1.5">
            <SkBar width="50%" height="10px" />
            <SkBar width="80%" height="8px" />
          </div>
          <SkChip width="56px" height="18px" />
        </div>
      ))}
    </ChatCard>
  );
}
